class FreeShippingMeter extends HTMLElement {
  connectedCallback() {
    const progress = this.dataset.progress || 0;
    this.style.width = '0%';
    setTimeout(() => {
      this.style.width = `${progress}%`;
    }, 50);
  }
}
customElements.define('free-shipping-meter', FreeShippingMeter);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.updateFreeShippingMeter(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateFreeShippingMeter(parsedState) {
    const container = document.querySelector('.cart-drawer__free-shipping');
    if (!container) return;

    const threshold = parseFloat(container.dataset.threshold);
    if (!threshold || isNaN(threshold)) return;

    const cartTotal = parsedState.original_total_price;
    if (cartTotal === undefined || cartTotal === null) return;

    let progress = (cartTotal / threshold) * 100;

    let isUnlocked = false;
    if (progress >= 100) {
      progress = 100;
      isUnlocked = true;
    }

    const bar = container.querySelector('[data-free-shipping-bar]');
    const textContainer = container.querySelector('[data-free-shipping-text]');

    if (bar) {
      setTimeout(() => {
        bar.style.width = `${progress}%`;
      }, 50);
    }

    if (isUnlocked) {
      container.classList.add('is-unlocked');
      if (textContainer) {
        textContainer.innerHTML = '<span class="unlocked-text">You\'ve got free shipping!</span>';
      }
    } else {
      container.classList.remove('is-unlocked');
      if (textContainer) {
        const amountLeft = threshold - cartTotal;
        // Use Shopify's built-in money formatter if available, else fall back gracefully
        const formatted = typeof Shopify !== 'undefined' && Shopify.formatMoney
          ? Shopify.formatMoney(amountLeft, window.themeMoneyFormat || '₹{{amount}}')
          : `₹${(amountLeft / 100).toFixed(2)}`;
        textContainer.innerText = `You're ${formatted} away from free shipping!`;
      }
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // After CartDrawerItems re-renders the drawer HTML, refresh the shipping meter
  // so it doesn't show stale / NaN values from the previous DOM.
  renderContents(parsedState) {
    super.renderContents(parsedState);
    // Re-query the drawer element and delegate meter update after render
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.updateFreeShippingMeter === 'function') {
      cartDrawer.updateFreeShippingMeter(parsedState);
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
